/**
 * MODULE: platform/browser/frontend/hooks/useBrowserSession.ts
 * PURPOSE: Browser session lifecycle — create, resume after refresh, explicit destroy.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { apiV1Url, getAuthHeaders } from '@shared/lib/frontend/api/config';
import type { BrowserSession } from '../types/browser.types';
import { BROWSER_QUICK_START_URL } from '../data/browserMissions';
import {
  clearPersistedBrowserSession,
  persistBrowserSession,
} from '../utils/browserSessionPersistence';
import { isMeaningfulBrowserUrl, normalizeBrowserNavigateUrl } from '../utils/browserPageUrl';

const SESSION_API = '/platform/browser/sessions';

function readSessionApiError(body: Record<string, unknown>, fallback: string): string {
  const err = body.error;
  const detail = body.detail;
  if (typeof err === 'string' && err.trim()) return err;
  if (typeof detail === 'string' && detail.trim()) return detail;
  return fallback;
}

export type ResumeSessionResult =
  | { ok: true; session: BrowserSession }
  | { ok: false; reason: 'not_found' | 'error' };

export type NavigateSessionResult =
  | { ok: true; url: string }
  | { ok: false; error: string };

async function reconcileBrowserSessions(): Promise<void> {
  await fetch(apiV1Url(`${SESSION_API}/reconcile`), {
    method: 'POST',
    headers: getAuthHeaders(),
    credentials: 'include',
  }).catch(() => undefined);
}

async function warmupSession(sessionId: string, startUrl: string): Promise<void> {
  if (!startUrl.trim()) return;
  await fetch(apiV1Url(`${SESSION_API}/${encodeURIComponent(sessionId)}/warmup`), {
    method: 'POST',
    headers: getAuthHeaders(),
    credentials: 'include',
    body: JSON.stringify({ start_url: startUrl.trim() }),
  }).catch(() => undefined);
}

export function useBrowserSession() {
  const [session, setSession] = useState<BrowserSession | null>(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resumedSession, setResumedSession] = useState(false);
  const sessionRef = useRef<BrowserSession | null>(null);

  useEffect(() => {
    sessionRef.current = session;
  }, [session]);

  const applySession = useCallback((next: BrowserSession, startUrl?: string) => {
    setSession(next);
    const url = isMeaningfulBrowserUrl(next.currentUrl)
      ? next.currentUrl
      : (startUrl ?? next.currentUrl);
    persistBrowserSession(next.id, url);
  }, []);

  const createSession = useCallback(async (startUrl = BROWSER_QUICK_START_URL, replace = false) => {
    setIsConnecting(true);
    setError(null);
    setResumedSession(false);
    const existing = sessionRef.current;
    if (replace && existing) {
      try {
        await fetch(apiV1Url(`${SESSION_API}/${encodeURIComponent(existing.id)}`), {
          method: 'DELETE',
          headers: getAuthHeaders(),
          credentials: 'include',
        });
      } catch {
        // continue with new session even if cleanup fails
      }
      clearPersistedBrowserSession();
      setSession(null);
    }
    try {
      const res = await fetch(apiV1Url(SESSION_API), {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({ start_url: startUrl }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as Record<string, unknown>;
        throw new Error(readSessionApiError(body, `Failed to start session (${res.status})`));
      }
      const data = await res.json();
      const created = data.session as BrowserSession;
      applySession(created, startUrl);
      if (created.warmupRequired !== false) {
        void warmupSession(created.id, startUrl);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create browser session';
      setError(message);
      console.error('Failed to create browser session', err);
    } finally {
      setIsConnecting(false);
    }
  }, [applySession]);

  const resumeSession = useCallback(async (sessionId: string): Promise<ResumeSessionResult> => {
    setIsConnecting(true);
    setError(null);
    try {
      const res = await fetch(apiV1Url(`${SESSION_API}/${encodeURIComponent(sessionId)}`), {
        headers: getAuthHeaders(),
        credentials: 'include',
      });
      if (res.status === 404) {
        clearPersistedBrowserSession();
        void reconcileBrowserSessions();
        return { ok: false, reason: 'not_found' };
      }
      if (!res.ok) {
        clearPersistedBrowserSession();
        return { ok: false, reason: 'error' };
      }
      const data = await res.json();
      const restored = data.session as BrowserSession;
      applySession(restored);
      setResumedSession(true);
      return { ok: true, session: restored };
    } catch (err) {
      clearPersistedBrowserSession();
      console.error('Failed to resume browser session', err);
      return { ok: false, reason: 'error' };
    } finally {
      setIsConnecting(false);
    }
  }, [applySession]);

  const destroySession = useCallback(async () => {
    const current = sessionRef.current;
    clearPersistedBrowserSession();
    setResumedSession(false);
    if (!current) return;
    try {
      await fetch(apiV1Url(`${SESSION_API}/${encodeURIComponent(current.id)}`), {
        method: 'DELETE',
        headers: getAuthHeaders(),
        credentials: 'include',
      });
    } catch (err) {
      console.error('Failed to destroy browser session', err);
    }
    setSession(null);
  }, []);

  const clearSessionLocally = useCallback(() => {
    clearPersistedBrowserSession();
    setSession(null);
    setResumedSession(false);
    setError(null);
  }, []);

  const patchSession = useCallback((patch: Partial<BrowserSession>) => {
    setSession((prev) => {
      if (!prev) return prev;
      const next = { ...prev, ...patch };
      if (patch.currentUrl || patch.id) {
        persistBrowserSession(next.id, next.currentUrl);
      }
      return next;
    });
  }, []);

  const navigateSession = useCallback(async (targetUrl: string): Promise<NavigateSessionResult> => {
    const current = sessionRef.current;
    if (!current?.id) {
      return { ok: false, error: 'No active browser session' };
    }
    const url = normalizeBrowserNavigateUrl(targetUrl);
    if (!url) {
      return { ok: false, error: 'Enter a valid http or https URL' };
    }
    try {
      const res = await fetch(
        apiV1Url(`${SESSION_API}/${encodeURIComponent(current.id)}/warmup`),
        {
          method: 'POST',
          headers: getAuthHeaders(),
          credentials: 'include',
          body: JSON.stringify({ start_url: url }),
        },
      );
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as Record<string, unknown>;
        throw new Error(readSessionApiError(body, `Navigation failed (${res.status})`));
      }
      patchSession({ currentUrl: url });
      persistBrowserSession(current.id, url);
      return { ok: true, url };
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Navigation failed';
      return { ok: false, error: message };
    }
  }, [patchSession]);

  return {
    session,
    isConnecting,
    error,
    resumedSession,
    createSession,
    resumeSession,
    navigateSession,
    destroySession,
    clearSessionLocally,
    patchSession,
  };
}
